import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    holidayPayload,
    holidayToForm,
    validateHoliday,
} from '../../helpers/holidays'
import HolidayService from '../../services/holidayService'
import { errorMessage } from '../../services/axios/errorMessage'
import HolidayForm from './HolidayForm'

/**
 * Edit a holiday: name, date, comms days and time, reminder, countries.
 *
 * Sends the whole holiday to PUT /comms/holidays/edit/{id}, which replaces
 * every field (a field left out would be wiped), keeping its status.
 */
const EditHolidayDialog = ({ holiday, onClose, onSaved }) => {
    const [form, setForm] = useState(() => holidayToForm(holiday))
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setForm(holidayToForm(holiday))
        setError('')
    }, [holiday])

    const onSave = async () => {
        const problem = validateHoliday(form, { original: holiday })
        if (problem) {
            setError(problem)
            return
        }
        setSaving(true)
        setError('')
        try {
            await HolidayService.update(
                holiday.holiday_id,
                holidayPayload(form, holiday)
            )
            onSaved()
        } catch (err) {
            setError(errorMessage(err?.response))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog
            open
            onClose={saving ? undefined : onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="edit-holiday-title"
        >
            <DialogTitle id="edit-holiday-title" sx={{ typography: 'h4' }}>
                Edit {holiday.holiday_name || holiday.holiday_id}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <HolidayForm
                        form={form}
                        onChange={(patch) =>
                            setForm((prev) => ({ ...prev, ...patch }))
                        }
                    />
                </Box>
                {error && (
                    <Box sx={{ mt: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={onSave} disabled={saving}>
                    {saving ? <CircularProgress size={16} /> : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

EditHolidayDialog.propTypes = {
    holiday: PropTypes.shape({
        holiday_id: PropTypes.string.isRequired,
        holiday_name: PropTypes.string,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
}

export default EditHolidayDialog
